import React from "react";
import { Button } from "@/components/ui/button";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CartItemFabric } from "@/types/order";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import SofaBlueprint from "@/configurator/SofaBlueprint";
import { SheetScrollableContent } from "@/components/ui/sheet-scrollable-content";

interface ConfigurationDrawerProps {
  data: {
    productId: string;
    cartItemFabrics?: CartItemFabric[];
    productName: string;
    fabricCombination?: {
      fabricCombinationProfiles: {
        name: string;
        language: string;
      }[];
    };
    selected_sofa_combinations?: string;
    additional_components?: Array<{
      id: string;
      additionalComponentGroupId: string;
      additional_component_profiles: Array<{
        id: string;
        name: string;
        material_name: string;
        description: string;
        language: string;
      }>;
      component_sku: string;
      code?: string;
      color?: {
        id: string;
        hex: string;
        background: string;
      };
      image?: {
        id: string;
        src: string;
        src_thumbnail: string;
      };
      additional_component_group: {
        id: string;
        use_fabric_prices_for_components: boolean;
        enable_custom_dimensions_for_components: boolean;
        enabled_dimensions_in_group: string[];
        additional_component_group_profiles: Array<{
          id: string;
          name: string;
          description: string;
          language: string;
        }>;
      };
      dimensions?: {
        id: string;
        width?: number;
        height?: number;
        length?: number;
        [key: string]: any;
      };
    }>;
  };
  onDirtyChange: (dirty: boolean) => void;
  onRequestClose: () => void;
}

export function ConfigurationDrawer({
  data,
  onDirtyChange,
  onRequestClose,
}: ConfigurationDrawerProps) {
  // Set dirty state to false since this drawer doesn't have editable content
  React.useEffect(() => {
    onDirtyChange(false);
  }, [onDirtyChange]);

  const {
    cartItemFabrics = [],
    productName,
    fabricCombination,
    selected_sofa_combinations,
    additional_components = [],
  } = data;

  // Parse the selected_sofa_combinations JSON string if it exists
  const parsedCombinations = React.useMemo(() => {
    if (!selected_sofa_combinations) return null;
    try {
      const parsed = JSON.parse(selected_sofa_combinations);
      console.log("Parsed sofa combinations:", parsed);
      return parsed;
    } catch (error) {
      console.error("Error parsing sofa combinations:", error);
      return null;
    }
  }, [selected_sofa_combinations]);

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Configuration - {productName}</SheetTitle>
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        {parsedCombinations && Array.isArray(parsedCombinations) && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Combinations</h3>
            <div className="space-y-6">
              {parsedCombinations.map((sofa, sofaIndex) => {
                // Each sofa is an array of modules
                return (
                  <div key={sofaIndex} className="border rounded-md p-4">
                    <div className="text-sm font-medium mb-3">
                      Set {sofaIndex + 1}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Array.isArray(sofa) &&
                        sofa.map((moduleData, moduleIndex) => {
                          try {
                            // Parse the module data if it's a string
                            const parsedModule =
                              typeof moduleData === "string"
                                ? JSON.parse(moduleData)
                                : moduleData;

                            return (
                              <>
                                {parsedModule.attrs.type && (
                                  <div className="flex flex-col">
                                    <SofaBlueprint
                                      id={moduleIndex + 1}
                                      dimensions={
                                        parsedModule.attrs.originalSofaForm
                                          .dimensions
                                      }
                                      type={parsedModule.attrs.type}
                                      containerHeight="100px"
                                      showMetrics={false}
                                    />
                                    <div className="mt-2 text-xs text-gray-600">
                                      <div>
                                        <span className="font-medium">
                                          Name:{" "}
                                        </span>
                                        {parsedModule.attrs.originalSofaForm
                                          .name || "N/A"}
                                      </div>
                                      <div>
                                        <span className="font-medium">
                                          Code:{" "}
                                        </span>
                                        {parsedModule.attrs.originalSofaForm
                                          .code || "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          } catch (error) {
                            console.error(
                              `Error parsing module ${moduleIndex}:`,
                              error
                            );
                            return (
                              <div
                                key={moduleIndex}
                                className="border rounded p-2 text-red-500 text-xs"
                              >
                                Error parsing module data
                              </div>
                            );
                          }
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <h3 className="text-lg font-semibold mb-2">Fabrics</h3>
        {fabricCombination && fabricCombination.fabricCombinationProfiles && (
          <div className="mb-4 text-sm text-gray-600">
            <span className="font-medium">Set: </span>
            {fabricCombination.fabricCombinationProfiles.find(
              (profile) => profile.language === "en"
            )?.name ||
              fabricCombination.fabricCombinationProfiles[0]?.name ||
              "Unknown"}
          </div>
        )}
        {cartItemFabrics && cartItemFabrics.length > 0 ? (
          cartItemFabrics.map((fabric, index) => {
            const fabricLetter = String.fromCharCode(65 + index); // A, B, C, ...
            const fabricGroupName =
              fabric.fabric_group.fabric_group_profiles.find(
                (profile) => profile.language === "en"
              )?.name ||
              fabric.fabric_group.fabric_group_profiles[0]?.name ||
              "";

            return (
              <div
                key={fabric.id}
                className="mb-4 border rounded-lg overflow-hidden shadow-sm"
              >
                <div className="flex">
                  <div className="w-[140px] h-[140px] flex-shrink-0">
                    {fabric.fabric?.image?.src_thumbnail ? (
                      <img
                        src={fabric.fabric.image.src_thumbnail}
                        alt={`Fabric ${fabricLetter}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No image</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow text-gray-600 text-sm">
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-semibold px-4 py-3 border-b bg-gray-50">
                            Fabric: {fabricLetter}
                          </TableCell>
                          <TableCell className="px-4 py-3 border-b bg-gray-50"></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium px-4 py-2 border-b w-1/2">
                            Fabric group
                          </TableCell>
                          <TableCell className="px-4 py-2 border-b text-right">
                            {fabricGroupName} <span className="font-mono ml-2 px-2 py-0.5 bg-gray-100 rounded">{fabric.fabric_group?.code || '-'}</span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium px-4 py-2 border-b">
                            Color name
                          </TableCell>
                          <TableCell className="px-4 py-2 border-b text-right">
                            {fabric.fabric?.color_name || '-'} <span className="font-mono ml-2 px-2 py-0.5 bg-gray-100 rounded">{fabric.fabric?.code}</span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium px-4 py-2">
                            Fabric type
                          </TableCell>
                          <TableCell className="px-4 py-2 text-right">
                            {fabric.fabric_group?.type}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center text-gray-500">
            No fabric information available
          </div>
        )}

        {additional_components.length > 0 && (
          <>
            <h3 className="text-lg font-semibold mb-2 mt-6">
              Additional Components
            </h3>
            <div className="border rounded-md overflow-hidden">
              <Table className="text-gray-500 text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Image</TableHead>
                    <TableHead>Component</TableHead>
                    <TableHead>Selected Option</TableHead>
                    <TableHead>Code</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {additional_components.map((component) => {
                    console.log("component", component);
                    // Find the English profile or use the first one
                    const groupProfile =
                      component.additional_component_group.additional_component_group_profiles.find(
                        (profile) => profile.language === "en"
                      ) ||
                      component.additional_component_group
                        .additional_component_group_profiles[0];

                    const componentProfile =
                      component.additional_component_profiles.find(
                        (profile) => profile.language === "en"
                      ) || component.additional_component_profiles[0];

                    return (
                      <TableRow key={component.id}>
                        <TableCell className="p-2">
                          {component.image?.src_thumbnail ? (
                            <div className="w-[60px] h-[60px]">
                              <img
                                src={component.image.src_thumbnail}
                                alt={componentProfile?.name || "Component"}
                                className="w-full h-full object-cover rounded-md"
                              />
                            </div>
                          ) : (
                            <div className="w-[60px] h-[60px] bg-gray-100 flex items-center justify-center rounded-md">
                              <span className="text-gray-400 text-xs">
                                No image
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="p-2">
                          {groupProfile?.name || "Unknown Group"}
                        </TableCell>
                        <TableCell className="p-2">
                          {componentProfile?.name || "N/A"}
                          {component.color && (
                            <div className="flex items-center mt-1">
                              <div
                                className="w-3 h-3 rounded-full mr-1"
                                style={{
                                  backgroundColor: component.color.hex,
                                }}
                              ></div>
                              <span className="text-xs text-gray-400">
                                {component.color.hex}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="p-2">
                          {component.code || "N/A"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </SheetScrollableContent>
    </SheetContent>
  );
}
